// Mode démonstration : tout se passe dans le navigateur (localStorage), synchronisé entre onglets.
// Il reproduit les règles de la base (statut des jobs, publication, codes opérateurs, aléas).
import { ApiError, uid, type Api, type ExportData, type JobInput, type NewTask, type PlanSlot, type Session } from "./api.ts";
import { jobStatus, makeDemo, type DemoDb } from "./demoData.ts";
import type { CatalogKind, DeviceAction, DeviceState, DocKind, DocPage, Job, JobDocument, Machine, Operator, Organization, Role, Task, TaskWithJob } from "./types.ts";

const KEY = "ordopli-demo-v2";
const ROLE_KEY = "ordopli-demo-role";

function load(): DemoDb {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as DemoDb;
  } catch { /* stockage indisponible */ }
  return makeDemo();
}

export class DemoApi implements Api {
  readonly mode = "demo" as const;
  private pinFails = new Map<string, number[]>();
  private db: DemoDb;
  private listeners = new Set<() => void>();
  private channel: BroadcastChannel | null = null;

  constructor() {
    this.db = load();
    this.persist(false);
    try {
      this.channel = new BroadcastChannel("ordopli-demo");
      this.channel.onmessage = () => { this.db = load(); this.emit(); };
    } catch { this.channel = null; }
    window.addEventListener("storage", (e) => { if (e.key === KEY) { this.db = load(); this.emit(); } });
  }

  reset(): void {
    this.db = makeDemo();
    this.persist();
  }

  /** Enregistre ; renvoie false seulement si le navigateur manque de place. */
  private persist(broadcast = true): boolean {
    let ok = true;
    try { localStorage.setItem(KEY, JSON.stringify(this.db)); }
    catch (e) { if ((e as Error).name === "QuotaExceededError") ok = false; }
    if (broadcast && ok) { this.channel?.postMessage("change"); this.emit(); }
    return ok;
  }

  private emit(): void { this.listeners.forEach((f) => f()); }
  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }

  private touchJob(jobId: string): void {
    const job = this.db.jobs.find((j) => j.id === jobId);
    if (!job) return;
    job.status = jobStatus(this.db.tasks.filter((t) => t.job_id === jobId), job.status);
    job.updated_at = new Date().toISOString();
  }

  // --- compte ---------------------------------------------------------------
  session(): Session { return { user_id: "demo", email: "demo@ordopli.fr" }; }
  async signIn(): Promise<void> {}
  async signUp(): Promise<"connecte"> { return "connecte"; }
  async resetPassword(): Promise<void> {}
  async updatePassword(): Promise<void> {}
  takeLinkNotice() { return null; }
  async signOut(): Promise<void> {}
  /** En démo, on peut changer de rôle pour voir ce que chacun peut faire. */
  role(): Role { try { return (localStorage.getItem(ROLE_KEY) as Role) || "admin"; } catch { return "admin"; } }
  setRole(r: Role): void { try { localStorage.setItem(ROLE_KEY, r); } catch { /* ignore */ } this.persist(); }
  async loadOrg(): Promise<Organization> { return { ...this.db.org, my_role: this.role() }; }
  async createOrg(): Promise<Organization> { return this.loadOrg(); }
  async updateOrg(patch: Partial<Organization>): Promise<void> { Object.assign(this.db.org, patch); this.persist(); }
  async members() { return this.db.members; }
  async updateMember(userId: string, role: Role) { const m = this.db.members.find((x) => x.user_id === userId); if (m) m.role = role; this.persist(); }
  async removeMember(userId: string) { this.db.members = this.db.members.filter((x) => x.user_id !== userId); this.persist(); }
  async invitations() { return this.db.invitations; }
  async invite(email: string, role: Role) {
    const e = email.trim().toLowerCase();
    this.db.invitations = [...this.db.invitations.filter((i) => i.email !== e), { org_id: this.db.org.id, email: e, role, created_at: new Date().toISOString() }];
    this.persist();
  }
  async cancelInvite(email: string) { this.db.invitations = this.db.invitations.filter((i) => i.email !== email); this.persist(); }
  async catalog(kind?: CatalogKind) { return this.db.catalog.filter((c) => !kind || c.kind === kind).sort((a, b) => a.position - b.position); }
  async replaceCatalog(kind: CatalogKind, scope: string, machineId: string | null, labels: string[]) {
    const same = (c: { kind: string; scope: string; machine_id: string | null }) => c.kind === kind && c.scope === scope && c.machine_id === machineId;
    this.db.catalog = [...this.db.catalog.filter((c) => !same(c)),
      ...labels.map((label, position) => ({ id: uid(), org_id: this.db.org.id, kind, scope, machine_id: machineId, label, position }))];
    this.persist();
  }

  // --- atelier --------------------------------------------------------------
  async machines(): Promise<Machine[]> { return [...this.db.machines].sort((a, b) => a.position - b.position); }
  async saveMachine(m: Partial<Machine> & { name: string }): Promise<Machine> {
    let row = m.id ? this.db.machines.find((x) => x.id === m.id) : undefined;
    if (row) Object.assign(row, m);
    else {
      row = { id: uid(), org_id: this.db.org.id, type: "plieuse", operations: ["pli"], formats: [], speed_per_hour: 6000,
        setup_min: 30, position: this.db.machines.length, active: true, specs: {}, ...m } as Machine;
      this.db.machines.push(row);
    }
    this.persist();
    return row;
  }
  async deleteMachine(id: string): Promise<void> {
    this.db.machines = this.db.machines.filter((m) => m.id !== id);
    for (const t of this.db.tasks) {
      if (t.machine_id === id) Object.assign(t, { machine_id: null, planned_start: null, planned_end: null, status: t.status === "planifie" ? "a_planifier" : t.status });
      if (t.pub_machine_id === id) Object.assign(t, { pub_machine_id: null, pub_start: null });
    }
    this.db.devices = this.db.devices.filter((d) => d.machine_id !== id);
    this.persist();
  }
  async operators(): Promise<Operator[]> { return [...this.db.operators].sort((a, b) => a.name.localeCompare(b.name)); }
  async saveOperator(o: Partial<Operator> & { name: string }, pin?: string): Promise<Operator> {
    if (pin !== undefined && pin !== "" && !/^[0-9]{4}$/.test(pin)) throw new ApiError("Le code doit faire 4 chiffres");
    let row = o.id ? this.db.operators.find((x) => x.id === o.id) : undefined;
    if (row) Object.assign(row, o);
    else { row = { id: uid(), org_id: this.db.org.id, machine_ids: [], active: true, role: "conducteur", ...o } as Operator; this.db.operators.push(row); }
    if (pin) this.db.pins[row.id] = pin;
    this.persist();
    return row;
  }
  async deleteOperator(id: string): Promise<void> {
    this.db.operators = this.db.operators.filter((o) => o.id !== id); delete this.db.pins[id]; this.persist();
  }

  // --- jobs -----------------------------------------------------------------
  async jobs(): Promise<Job[]> { return [...this.db.jobs].sort((a, b) => a.due_at.localeCompare(b.due_at)); }
  async job(id: string) {
    const job = this.db.jobs.find((j) => j.id === id);
    return job ? { job, tasks: this.db.tasks.filter((t) => t.job_id === id).sort((a, b) => a.seq - b.seq) } : null;
  }
  async saveJob(id: string | null, input: JobInput, tasks: NewTask[]): Promise<Job> {
    const now = new Date().toISOString();
    let job = id ? this.db.jobs.find((j) => j.id === id) : undefined;
    if (job) Object.assign(job, input, { updated_at: now });
    else {
      job = { id: uid(), org_id: this.db.org.id, status: "a_planifier", created_at: now, updated_at: now, ...input };
      this.db.jobs.push(job);
    }
    const existing = this.db.tasks.filter((t) => t.job_id === job!.id);
    const keep = new Set(tasks.map((t) => t.id).filter(Boolean));
    this.db.tasks = this.db.tasks.filter((t) => t.job_id !== job!.id || keep.has(t.id) || !["a_planifier", "planifie"].includes(t.status));
    tasks.forEach((nt, i) => {
      const cur = nt.id ? existing.find((t) => t.id === nt.id) : undefined;
      if (cur) Object.assign(cur, nt, { seq: i + 1, updated_at: now });
      else this.db.tasks.push(blankTask(this.db.org.id, job!.id, i + 1, nt, now));
    });
    this.touchJob(job.id);
    this.persist();
    return job;
  }
  async cancelJob(id: string): Promise<void> {
    const job = this.db.jobs.find((j) => j.id === id);
    if (job) { job.status = "annule"; job.updated_at = new Date().toISOString(); }
    this.persist();
  }
  async deleteJob(id: string): Promise<void> {
    this.db.jobs = this.db.jobs.filter((j) => j.id !== id);
    this.db.tasks = this.db.tasks.filter((t) => t.job_id !== id);
    this.db.documents = (this.db.documents || []).filter((d) => d.job_id !== id);
    this.persist();
  }

  // --- documents du job --------------------------------------------------------
  private docs() { return (this.db.documents ||= []); }
  private bare(d: JobDocument & { pageData?: DocPage[] }): JobDocument {
    const { pageData: _p, ...rest } = d;
    return rest;
  }
  async documents(jobId: string): Promise<JobDocument[]> {
    return this.docs().filter((d) => d.job_id === jobId).map((d) => this.bare(d));
  }
  async documentPage(docId: string, page: number): Promise<string> {
    const d = this.docs().find((x) => x.id === docId);
    const p = d?.pageData[page - 1];
    if (!p) throw new ApiError("Page introuvable", 404);
    return p.data;
  }
  async addDocument(jobId: string, doc: { name: string; kind: DocKind; thumb: string; pages: DocPage[] }): Promise<JobDocument> {
    const row = { id: uid(), job_id: jobId, name: doc.name.slice(0, 200), kind: doc.kind, pages: doc.pages.length, thumb: doc.thumb, created_at: new Date().toISOString(), pageData: doc.pages };
    this.docs().push(row);
    if (!this.persist()) {
      this.db.documents = this.docs().filter((d) => d.id !== row.id);
      throw new ApiError("La démonstration garde peu de place dans le navigateur : ce document est trop lourd. Essayez une image plus petite ou un PDF d'une ou deux pages.");
    }
    return this.bare(row);
  }
  async updateDocument(id: string, patch: Partial<Pick<JobDocument, "name" | "kind">>): Promise<void> {
    const d = this.docs().find((x) => x.id === id);
    if (d) Object.assign(d, patch);
    this.persist();
  }
  async deleteDocument(id: string): Promise<void> {
    this.db.documents = this.docs().filter((d) => d.id !== id);
    this.persist();
  }

  // --- planning -------------------------------------------------------------
  async tasks(since?: Date): Promise<TaskWithJob[]> {
    const jobs = new Map(this.db.jobs.map((j) => [j.id, j]));
    return this.db.tasks
      .filter((t) => t.status !== "termine" || !since || (t.finished_at && new Date(t.finished_at) >= since))
      .filter((t) => jobs.has(t.job_id))
      .map((t) => ({ ...t, job: jobs.get(t.job_id)! }));
  }
  async savePlan(slots: PlanSlot[]): Promise<void> {
    const now = new Date().toISOString();
    for (const s of slots) {
      const t = this.db.tasks.find((x) => x.id === s.task_id);
      if (!t) continue;
      Object.assign(t, {
        machine_id: s.machine_id, planned_start: s.planned_start, planned_end: s.planned_end,
        setup_min: s.setup_min, run_min: s.run_min, updated_at: now,
      });
      if (s.locked !== undefined) t.locked = s.locked;
      if (s.operator_id !== undefined) t.operator_id = s.operator_id;
      if (s.manual_run_min !== undefined) t.manual_run_min = s.manual_run_min;
      if (t.status === "a_planifier" || t.status === "planifie") t.status = s.machine_id ? "planifie" : "a_planifier";
      this.touchJob(t.job_id);
    }
    this.persist();
  }
  async publishPlan(): Promise<number> {
    let n = 0;
    for (const t of this.db.tasks) {
      if (t.status === "termine") continue;
      if (t.pub_machine_id !== t.machine_id || t.pub_start !== t.planned_start) {
        t.pub_machine_id = t.machine_id; t.pub_start = t.planned_start; n++;
      }
    }
    this.persist();
    return n;
  }

  // --- suivi ------------------------------------------------------------------
  async incidents(since: Date) { return this.db.incidents.filter((i) => !i.ended_at || new Date(i.started_at) >= since); }
  async events(since: Date) { return this.db.events.filter((e) => new Date(e.at) >= since); }
  async exportData(): Promise<ExportData> {
    return {
      jobs: [...this.db.jobs], tasks: [...this.db.tasks], incidents: [...this.db.incidents], events: [...this.db.events],
      machines: await this.machines(), operators: await this.operators(),
    };
  }

  // --- tablettes ------------------------------------------------------------
  async devices() { return this.db.devices.map(({ token: _t, ...d }) => d); }
  async createPairingCode(machineId: string): Promise<string> {
    const code = String(Math.floor(Math.random() * 1e8)).padStart(8, "0");
    this.db.pairing.push({ code, machine_id: machineId, expires_at: new Date(Date.now() + 15 * 60_000).toISOString() });
    this.persist();
    return code;
  }
  async deleteDevice(id: string): Promise<void> { this.db.devices = this.db.devices.filter((d) => d.id !== id); this.persist(); }
  async pairDevice(code: string, name: string): Promise<string> {
    const p = this.db.pairing.find((x) => x.code === code.replace(/\D/g, "") && new Date(x.expires_at) > new Date());
    if (!p) throw new ApiError("Code invalide ou expiré");
    this.db.pairing = this.db.pairing.filter((x) => x !== p);
    const token = uid();
    this.db.devices.push({ id: uid(), org_id: this.db.org.id, machine_id: p.machine_id, name: name || "Tablette", token, created_at: new Date().toISOString(), last_seen_at: null });
    this.persist();
    return token;
  }
  /** En démo, la tablette peut aussi se rattacher directement à une machine. */
  pairMachine(machineId: string): string {
    const token = uid();
    this.db.devices.push({ id: uid(), org_id: this.db.org.id, machine_id: machineId, name: "Tablette (démo)", token, created_at: new Date().toISOString(), last_seen_at: null });
    this.persist();
    return token;
  }
  async deviceDocuments(token: string, jobId: string): Promise<JobDocument[]> { this.device(token); return this.documents(jobId); }
  async deviceDocumentPage(token: string, docId: string, page: number): Promise<string> { this.device(token); return this.documentPage(docId, page); }
  private device(token: string) {
    const d = this.db.devices.find((x) => x.token === token);
    if (!d) throw new ApiError("Tablette non reconnue", 401);
    d.last_seen_at = new Date().toISOString();
    return d;
  }
  async deviceState(token: string): Promise<DeviceState> {
    const d = this.device(token);
    const jobs = new Map(this.db.jobs.map((j) => [j.id, j]));
    const withJob = (t: Task): TaskWithJob => ({ ...t, job: jobs.get(t.job_id)! });
    const live = this.db.tasks
      .filter((t) => t.machine_id === d.machine_id && ["calage", "production", "pause"].includes(t.status))
      .sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""))[0];
    const queue = this.queue(d.machine_id);
    const current = live || queue[0] || null;
    const machine = this.db.machines.find((m) => m.id === d.machine_id)!;
    const from = new Date(); from.setHours(0, 0, 0, 0);
    const day = this.db.tasks.filter((t) => jobs.get(t.job_id)?.status !== "annule" && (
      (t.machine_id === d.machine_id && t.status === "termine" && t.finished_at && new Date(t.finished_at) >= from)
      || (t.machine_id === d.machine_id && ["calage", "production", "pause"].includes(t.status))
      || (t.pub_machine_id === d.machine_id && ["a_planifier", "planifie"].includes(t.status))))
      .sort((a, b) => (a.started_at || a.pub_start || "9").localeCompare(b.started_at || b.pub_start || "9")).slice(0, 30);
    const items = this.db.catalog.filter((c) => c.kind === "alea");
    return {
      org_name: this.db.org.name,
      machine,
      operators: this.db.operators
        .filter((o) => o.active && (!o.machine_ids.length || o.machine_ids.includes(d.machine_id) || o.role !== "conducteur"))
        .map((o) => ({ id: o.id, name: o.name, role: o.role })).sort((a, b) => a.name.localeCompare(b.name)),
      current: current ? withJob(current) : null,
      next: queue.filter((t) => t !== current).slice(0, 3).map(withJob),
      day: day.map(withJob),
      incident: this.db.incidents.find((i) => i.machine_id === d.machine_id && !i.ended_at) || null,
      aleas_type: items.filter((c) => !c.machine_id && c.scope === machine.type).sort((a, b) => a.position - b.position).map((c) => c.label),
      aleas_machine: items.filter((c) => c.machine_id === d.machine_id).sort((a, b) => a.position - b.position).map((c) => c.label),
      hours: this.db.org.hours,
      server_time: new Date().toISOString(),
    };
  }
  private queue(machineId: string): Task[] {
    const jobs = new Map(this.db.jobs.map((j) => [j.id, j]));
    return this.db.tasks
      .filter((t) => t.pub_machine_id === machineId && (t.status === "a_planifier" || t.status === "planifie") && jobs.get(t.job_id)?.status !== "annule")
      .sort((a, b) => (a.pub_start || "9").localeCompare(b.pub_start || "9"));
  }
  async deviceAction(token: string, operatorId: string, pin: string, a: DeviceAction | { type: "login" }): Promise<DeviceState> {
    const d = this.device(token);
    const op = this.db.operators.find((o) => o.id === operatorId && o.active);
    // comme en ligne : 5 erreurs pour un opérateur sur cette tablette = 15 minutes de blocage
    const key = `${d.id}:${operatorId}`, now0 = Date.now();
    const fails = (this.pinFails.get(key) || []).filter((t) => now0 - t < 15 * 60_000);
    if (fails.length >= 5) throw new ApiError(`Code opérateur bloqué après 5 erreurs : réessayez dans ${Math.max(1, Math.ceil((fails[fails.length - 1] + 15 * 60_000 - now0) / 60_000))} min.`, 429);
    if (!op || this.db.pins[op.id] !== pin) {
      fails.push(now0); this.pinFails.set(key, fails);
      const left = 5 - fails.length;
      throw new ApiError(left > 0 ? `Code opérateur incorrect (encore ${left} essai${left > 1 ? "s" : ""})` : "Code opérateur incorrect : bloqué 15 min après 5 erreurs.", 403);
    }
    this.pinFails.delete(key);
    const now = new Date().toISOString();
    if (a.type !== "login" && "task_id" in a && a.type !== "alea") {
      const t = this.db.tasks.find((x) => x.id === a.task_id && (x.machine_id === d.machine_id || x.pub_machine_id === d.machine_id));
      if (!t) throw new ApiError("Job introuvable sur cette machine");
      if (op.role === "conducteur") {
        if (t.operator_id && t.operator_id !== op.id) throw new ApiError("Ce job est attribué à un autre conducteur", 403);
        if (a.type === "calage" && (t.status === "a_planifier" || t.status === "planifie") && this.queue(d.machine_id)[0] !== t) {
          throw new ApiError("Seul un chef peut changer l'ordre des jobs", 403);
        }
      }
      if (a.type === "calage") Object.assign(t, { status: "calage", machine_id: d.machine_id, started_at: t.started_at || now });
      if (a.type === "production" || a.type === "reprise") Object.assign(t, { status: "production", machine_id: d.machine_id, started_at: t.started_at || now });
      if (a.type === "pause") t.status = "pause";
      if (a.type === "quantite") t.produced_qty = Math.max(0, a.qty);
      if (a.type === "termine") {
        Object.assign(t, { status: "termine", finished_at: now, produced_qty: Math.max(0, a.qty), waste_qty: Math.max(0, a.waste) });
        for (const i of this.db.incidents) if (i.machine_id === d.machine_id && !i.ended_at) i.ended_at = now;
      }
      t.updated_at = now;
      this.db.events.push({ id: uid(), org_id: this.db.org.id, task_id: t.id, machine_id: d.machine_id, operator_id: op.id, kind: a.type, qty: "qty" in a ? a.qty : null, at: now });
      this.touchJob(t.job_id);
    } else if (a.type === "alea") {
      if (!this.db.incidents.some((i) => i.machine_id === d.machine_id && !i.ended_at)) {
        this.db.incidents.push({ id: uid(), org_id: this.db.org.id, machine_id: d.machine_id, task_id: a.task_id, operator_id: op.id, kind: (a.kind || "Autre").slice(0, 80), comment: a.comment, started_at: now, ended_at: null });
      }
    } else if (a.type === "note_alea") {
      for (const i of this.db.incidents) if (i.machine_id === d.machine_id && !i.ended_at) i.comment = a.comment.slice(0, 500);
    } else if (a.type === "fin_alea") {
      for (const i of this.db.incidents) if (i.machine_id === d.machine_id && !i.ended_at) i.ended_at = now;
    }
    this.persist();
    return this.deviceState(token);
  }
}

export function blankTask(orgId: string, jobId: string, seq: number, nt: NewTask, now: string): Task {
  return {
    org_id: orgId, job_id: jobId, seq, machine_id: null, planned_start: null, planned_end: null,
    setup_min: null, run_min: null, locked: false, pub_machine_id: null, pub_start: null, status: "a_planifier",
    produced_qty: 0, waste_qty: 0, started_at: null, finished_at: null, updated_at: now, operator_id: null,
    ...nt, id: nt.id || uid(),
  } as Task;
}
